import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Trophy, BarChart3, ShieldCheck, LogOut, User, Sun, Moon, ArrowLeft, Users
} from 'lucide-react';

const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_leadership-hub-59/artifacts/yr2ulz5t_Brainayan-Logo-HighRes.png';
const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_leadership-hub-59/artifacts/ugsfbpeg_Brainayan%20Logo%20-Light-%20Transparent_BG.png';

export default function Navbar({ showBack, backTo, backLabel }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  return (
    <nav className="border-b border-border bg-surface sticky top-0 z-30" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {showBack && (
            <button data-testid="nav-back-btn" onClick={() => navigate(backTo || '/')}
              className="w-8 h-8 border border-border rounded-lg flex items-center justify-center hover:bg-muted transition-colors mr-1">
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="nav-logo">
            <img
              src={theme === 'dark' ? LOGO_LIGHT : LOGO_DARK}
              alt="Brainayan"
              className="h-7 sm:h-8 w-auto"
            />
          </button>
          {backLabel && (
            <span className="text-sm font-medium text-muted-foreground hidden sm:block">{backLabel}</span>
          )}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <Button data-testid="leaderboard-nav-button" onClick={() => navigate('/leaderboard')}
            className="h-8 sm:h-9 px-2 sm:px-3 bg-transparent border border-border text-foreground hover:bg-muted gap-1.5 text-xs sm:text-sm font-medium rounded-lg shadow-none">
            <Trophy className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Leaderboard</span>
          </Button>
          <Button data-testid="progress-nav-button" onClick={() => navigate('/progress')}
            className="h-8 sm:h-9 px-2 sm:px-3 bg-transparent border border-border text-foreground hover:bg-muted gap-1.5 text-xs sm:text-sm font-medium rounded-lg shadow-none">
            <BarChart3 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Progress</span>
          </Button>
          {['super_admin', 'org_admin', 'manager'].includes(user?.role) && (
            <Button data-testid="manager-nav-button" onClick={() => navigate('/manager')}
              className="h-8 sm:h-9 px-2 sm:px-3 bg-transparent border border-border text-foreground hover:bg-muted gap-1.5 text-xs sm:text-sm font-medium rounded-lg shadow-none">
              <Users className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Team</span>
            </Button>
          )}
          {['super_admin', 'org_admin'].includes(user?.role) && (
            <Button data-testid="admin-nav-button" onClick={() => navigate('/admin')}
              className="h-8 sm:h-9 px-2 sm:px-3 bg-transparent border border-brand/30 text-brand hover:bg-brand-light gap-1.5 text-xs sm:text-sm font-medium rounded-lg shadow-none">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Admin</span>
            </Button>
          )}

          <button data-testid="theme-toggle" onClick={toggleTheme}
            className="w-8 h-8 sm:w-9 sm:h-9 border border-border rounded-lg flex items-center justify-center hover:bg-muted transition-colors">
            {theme === 'dark' ? <Sun className="w-4 h-4 text-warning" /> : <Moon className="w-4 h-4 text-subtle" />}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button"
                className="w-8 h-8 sm:w-9 sm:h-9 bg-brand text-[#333333] rounded-full flex items-center justify-center font-heading font-bold text-xs sm:text-sm transition-all hover:opacity-90">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="border border-border shadow-modal bg-surface w-56" align="end">
              <div className="px-3 py-2.5">
                <p className="font-heading font-bold text-sm">{user?.name}</p>
                <p className="font-body text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator className="bg-border" />
              <DropdownMenuItem className="gap-2 cursor-pointer font-body text-sm">
                <User className="w-4 h-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-menu-item" onClick={logout} className="gap-2 cursor-pointer font-body text-sm text-destructive">
                <LogOut className="w-4 h-4" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}
